<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">Calendario / Próximas fechas</h1>
          <p class="mt-1 text-sm text-gray-400">Torneos por jugar, sorteos, horarios y contactos.</p>
        </div>
        <div class="flex w-full gap-2 sm:w-auto">
          <router-link to="/admin/calendario/nuevo" class="btn-bochas-rojo w-full text-center text-sm sm:w-auto">
            + Nueva fecha
          </router-link>
        </div>
      </div>

      <div v-if="!loading && !error && eventos.length" class="mb-4 space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <input v-model="search" type="search" aria-label="Buscar por torneo o lugar"
            class="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-white placeholder-gray-500 outline-none transition-colors focus:border-bochas-rojo"
            placeholder="Buscar por torneo o lugar" />
          <SelectField v-model="filters.location" :options="locationOptions" aria-label="Filtrar por lugar" />
          <SelectField v-model="filters.status" :options="statusOptions" aria-label="Filtrar por estado" />
        </div>
        <div class="grid grid-cols-2 items-center gap-2 sm:grid-cols-[160px_160px_auto] sm:justify-start">
          <DatePickerField v-model="dateFrom" placeholder="Desde" aria-label="Fecha desde" />
          <DatePickerField v-model="dateTo" placeholder="Hasta" aria-label="Fecha hasta" />
          <button v-if="hasFilters" type="button" class="btn-bochas-trans text-sm" @click="clearFilters">Limpiar filtros</button>
        </div>
        <p class="text-sm text-gray-400" role="status">{{ sortedEventos.length }} de {{ eventos.length }} fechas</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4">
        {{ error }}
      </div>

      <!-- Empty -->
      <div v-if="!loading && !error && eventos.length === 0" class="text-center py-12">
        <p class="text-gray-400 mb-4">Aún no hay próximas fechas cargadas.</p>
        <router-link to="/admin/calendario/nuevo" class="btn-bochas-rojo inline-block">
          Crear el primero
        </router-link>
      </div>

      <div v-else-if="!loading && !error && !sortedEventos.length" class="py-12 text-center">
        <p class="mb-4 text-gray-400">No hay fechas que coincidan con los filtros.</p>
        <button type="button" class="btn-bochas-trans text-sm" @click="clearFilters">Limpiar filtros</button>
      </div>

      <!-- Tabla desktop / Cards mobile -->
      <div v-else-if="!loading && !error">
        <div class="mb-3 flex flex-col gap-2 rounded-lg border border-white/10 bg-white/[0.03] p-3 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-sm text-gray-300">{{ selectedIds.length }} seleccionados</p>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn-bochas-trans px-3 py-2 text-xs" @click="toggleSelectAll">
              {{ allSelected ? "Quitar selección" : "Seleccionar página" }}
            </button>
            <button
              type="button"
              class="btn-bochas-rojo px-3 py-2 text-xs"
              :disabled="selectedIds.length === 0 || deletingMany"
              :class="{ 'opacity-60 cursor-not-allowed': selectedIds.length === 0 || deletingMany }"
              @click="onDeleteSelected"
            >
              {{ deletingMany ? "Eliminando..." : "Eliminar seleccionados" }}
            </button>
          </div>
        </div>

        <div class="hidden md:block card-fbochas overflow-hidden">
          <table class="w-full">
            <thead class="bg-white/5 border-b border-white/10">
              <tr class="text-left text-xs uppercase tracking-wide text-gray-400">
                <th class="px-4 py-3">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    class="h-4 w-4 rounded border-gray-700 bg-bochas-bg text-bochas-rojo focus:ring-bochas-rojo"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('title')">
                    Torneo / evento <span>{{ sortIcon("title") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('date')">
                    Fecha <span>{{ sortIcon("date") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('time')">
                    Hora <span>{{ sortIcon("time") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('location')">
                    Lugar <span>{{ sortIcon("location") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('status')">
                    Estado <span>{{ sortIcon("status") }}</span>
                  </button>
                </th>
                <th v-if="canViewRecordHistory" class="px-4 py-3">Registro</th>
                <th class="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              <tr v-for="e in paginatedItems" :key="e.id" class="hover:bg-white/5 transition-colors">
                <td class="px-4 py-3">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="e.id"
                    class="h-4 w-4 rounded border-gray-700 bg-bochas-bg text-bochas-rojo focus:ring-bochas-rojo"
                  />
                </td>
                <td class="px-4 py-3 text-white font-medium">{{ e.title }}</td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ formatDate(e.appointmentDay) }}</td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ e.appointmentTime || "—" }}</td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ e.location || "—" }}</td>
                <td class="px-4 py-3">
                  <span :class="['text-xs px-2 py-1 rounded', statusClass(e.status)]">
                    {{ statusLabel(e.status) }}
                  </span>
                </td>
                <td v-if="canViewRecordHistory" class="px-4 py-3"><AdminRecordDates v-if="canViewRecordHistory" resource="appointments" :record="e" /></td>
                <td class="px-4 py-3 text-right">
                  <div class="inline-flex items-center justify-end gap-2">
                    <router-link
                      :to="`/admin/calendario/editar/${e.id}`"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                      title="Editar"
                      aria-label="Editar evento"
                    ><IconFbochas name="edit" :size="16" /></router-link>
                    <button
                      @click="onDelete(e)"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                      title="Eliminar"
                      aria-label="Eliminar evento"
                    ><IconFbochas name="trash" :size="16" /></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden space-y-3">
          <div v-for="e in paginatedItems" :key="e.id" class="card-fbochas p-4 transition-shadow"
            :class="{ 'ring-1 ring-bochas-rojo': selectedIds.includes(e.id) }">
            <div class="flex items-start justify-between gap-2 mb-2">
              <div class="flex min-w-0 flex-1 items-start gap-2">
                <label class="relative -ml-2 -mt-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="e.id"
                    :aria-label="`Seleccionar ${e.title}`"
                    class="peer h-6 w-6 cursor-pointer appearance-none rounded-md border border-white/30 bg-white/5 transition-colors checked:border-bochas-rojo checked:bg-bochas-rojo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-bochas-bg"
                  />
                  <IconFbochas name="check" :size="16" aria-hidden="true" class="pointer-events-none absolute text-white opacity-0 peer-checked:opacity-100" />
                </label>
                <h3 class="text-white font-semibold flex-1">{{ e.title }}</h3>
              </div>
              <span :class="['text-xs px-2 py-1 rounded flex-shrink-0', statusClass(e.status)]">
                {{ statusLabel(e.status) }}
              </span>
            </div>
            <p class="text-xs text-gray-400 mb-3">
              📅 {{ formatDate(e.appointmentDay) }}
              <span v-if="e.appointmentTime"> · 🕐 {{ e.appointmentTime }} hs</span>
              <span v-if="e.location"> · 📍 {{ e.location }}</span>
            </p>
            <AdminRecordDates v-if="canViewRecordHistory" resource="appointments" :record="e" class="mb-3" />
            <div class="flex items-center justify-end gap-2">
              <router-link
                :to="`/admin/calendario/editar/${e.id}`"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
               title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
              <button
                @click="onDelete(e)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
               title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
            </div>
          </div>
        </div>
        <Paginacion :current-page="page" :total-items="sortedEventos.length" :per-page="pageSize" :scroll-to-top="false" @change="goPage" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { getStoredUser } from "@/services/auth/authService";
import { isGeneralAdmin } from "@/utils/roles";

import { computed, ref, onMounted, watch } from "vue";
import SelectField from "@/components/Common/SelectField.vue";
import DatePickerField from "@/components/Common/DatePickerField.vue";
import Paginacion from "@/components/Common/Paginacion.vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { listarEventosAdmin, eliminarEvento } from "@/services/appointments/appointmentsService";
import { formatDate } from "@/utils/date";
import { useConfirm } from "@/composables/useConfirm";
import { useListingState } from "@/composables/useListingState";
import AdminRecordDates from "@/components/Admin/AdminRecordDates.vue";

const canViewRecordHistory = computed(() => isGeneralAdmin(getStoredUser()));

const { confirm } = useConfirm();
const eventos = ref([]);
const loading = ref(true);
const error = ref("");
const page = ref(1);
const pageSize = 10;
const selectedIds = ref([]);
const deletingMany = ref(false);
const sortBy = ref("date");
const sortDirection = ref("asc");
const search = ref("");
const filters = ref({ location: "", status: "" });
const { from: dateFrom, to: dateTo } = useListingState(
  "admin:calendario:fechas", { from: "", to: "" }, { persist: true },
);
const hasFilters = computed(() => Boolean(search.value || dateFrom.value || dateTo.value || Object.values(filters.value).some(Boolean)));
const locationOptions = computed(() => [
  { value: "", label: "Todos los lugares" },
  ...[...new Set(eventos.value.map((item) => item.location).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, "es"))
    .map((location) => ({ value: location, label: location })),
]);
const statusOptions = [
  { value: "", label: "Todos los estados" },
  { value: "published", label: "Publicado" },
  { value: "draft", label: "Borrador" },
  { value: "cancelled", label: "Cancelado" },
];
function normalizeText(value) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}
const allSelected = computed(() => paginatedItems.value.length > 0 &&
  paginatedItems.value.every((item) => selectedIds.value.includes(item.id)));
const sortedEventos = computed(() => {
  const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
  const term = normalizeText(search.value);
  const { location, status } = filters.value;
  const from = dateFrom.value;
  const to = dateTo.value;
  const [start, end] = from && to && from > to ? [to, from] : [from, to];
  return eventos.value.filter((item) => {
    const day = String(item.appointmentDay || "").slice(0, 10);
    return (!term || normalizeText([item.title, item.location].join(" ")).includes(term)) &&
      (!location || item.location === location) &&
      (!status || item.status === status) &&
      (!start || day >= start) && (!end || (day && day <= end));
  }).sort((a, b) => {
    let result = 0;
    if (sortBy.value === "date") result = String(a.appointmentDay || "").localeCompare(String(b.appointmentDay || ""));
    else if (sortBy.value === "time") result = String(a.appointmentTime || "").localeCompare(String(b.appointmentTime || ""));
    else if (sortBy.value === "location") result = collator.compare(String(a.location || ""), String(b.location || ""));
    else if (sortBy.value === "status") result = collator.compare(String(a.status || ""), String(b.status || ""));
    else result = collator.compare(String(a.title || ""), String(b.title || ""));
    return sortDirection.value === "asc" ? result : -result;
  });
});

const totalPages = computed(() => Math.max(1, Math.ceil(sortedEventos.value.length / pageSize)));
const paginatedItems = computed(() => sortedEventos.value.slice((page.value - 1) * pageSize, page.value * pageSize));
watch([search, filters, dateFrom, dateTo, sortBy, sortDirection], () => {
  page.value = 1;
  selectedIds.value = [];
}, { deep: true, flush: "sync" });
watch(paginatedItems, (items) => {
  selectedIds.value = selectedIds.value.filter((id) => items.some((item) => item.id === id));
}, { flush: "sync" });
function clearFilters() {
  search.value = "";
  filters.value = { location: "", status: "" };
  dateFrom.value = "";
  dateTo.value = "";
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarEventosAdmin();
    eventos.value = data.items || [];
    page.value = Math.min(page.value, totalPages.value);
    selectedIds.value = selectedIds.value.filter((id) =>
      eventos.value.some((item) => item.id === id),
    );
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "Error al cargar.";
  } finally {
    loading.value = false;
  }
}

function goPage(nextPage) {
  page.value = Math.min(Math.max(nextPage, 1), totalPages.value);
  selectedIds.value = [];
}

function toggleSelectAll() {
  selectedIds.value = allSelected.value ? [] : paginatedItems.value.map((e) => e.id);
}

function setSort(key) {
  if (sortBy.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    return;
  }
  sortBy.value = key;
  sortDirection.value = "asc";
}

function sortIcon(key) {
  if (sortBy.value !== key) return "↕";
  return sortDirection.value === "asc" ? "↑" : "↓";
}

async function onDelete(e) {
  const confirmed = await confirm({
    title: "Eliminar evento",
    message: `¿Eliminar el evento "${e.title}"?`,
  });
  if (!confirmed) return;
  try {
    await eliminarEvento(e.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo eliminar.";
  }
}

async function onDeleteSelected() {
  if (!selectedIds.value.length) return;
  const confirmed = await confirm({
    title: "Eliminar eventos",
    message: `¿Eliminar ${selectedIds.value.length} eventos seleccionados?`,
  });
  if (!confirmed) return;
  deletingMany.value = true;
  try {
    await Promise.all(selectedIds.value.map((id) => eliminarEvento(id)));
    selectedIds.value = [];
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudieron eliminar.";
  } finally {
    deletingMany.value = false;
  }
}

function statusClass(s) {
  if (s === "published") return "bg-green-500/20 text-green-300";
  if (s === "cancelled") return "bg-red-500/20 text-red-300";
  return "bg-yellow-500/20 text-yellow-300";
}
function statusLabel(s) {
  if (s === "published") return "Publicado";
  if (s === "cancelled") return "Cancelado";
  return "Borrador";
}

onMounted(load);
</script>

<style scoped>
.table-sort-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
  transition: color 160ms ease;
}

.table-sort-button:hover {
  color: white;
}
</style>
